import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { formatGs, ROLE_LABELS } from "../lib/format";
import { BadgeDollarSign, Wallet, CalendarClock, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";

const EST_META = {
  pendiente: { label: "Pendiente", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  aprobada: { label: "Pagada", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rechazada: { label: "Rechazada", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function Comisiones() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const staff = ["admin", "encargado"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [ventana, setVentana] = useState({ abierta: false, dia: 0 });
  const [solis, setSolis] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/comisiones").then(({ data }) => setRows(data.comisiones || [])).catch(() => {});
    if (!staff) {
      api.get("/comisiones/ventana").then(({ data }) => setVentana(data)).catch(() => {});
      api.get("/comisiones/solicitudes").then(({ data }) => setSolis(data)).catch(() => {});
    }
  }, [staff]);
  useEffect(() => { load(); }, [load]);

  const mine = !staff ? rows.find((c) => c.cobrador_id === user?.id) : null;

  const solicitar = async () => {
    setLoading(true);
    try { const { data } = await api.post("/comisiones/solicitar"); toast.success(`Solicitud enviada: ${formatGs(data.monto)}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Header title="Comisiones y pagos" subtitle={staff ? "Comisiones del equipo" : "Tu resumen de comisiones"} onMenu={() => setMobileOpen(true)} />
      <main className="p-4 sm:p-6 lg:p-8 space-y-5">
        {/* Vista comisionista */}
        {!staff && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-emerald-200 p-5 shadow-sm" data-testid="mi-comision-total">
                <BadgeDollarSign className="h-6 w-6 text-emerald-600" />
                <div className="text-2xl font-extrabold text-[#0A1F44] mt-3">{formatGs(mine?.comision || 0)}</div>
                <div className="text-sm text-[#64748B]">Comisión acumulada</div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-[#0A1F44]" />
                <div className="text-2xl font-extrabold text-[#0A1F44] mt-3">{formatGs(mine?.pagado || 0)}</div>
                <div className="text-sm text-[#64748B]">Pagado</div>
              </div>
              <div className="bg-white rounded-2xl border border-amber-200 p-5 shadow-sm" data-testid="mi-comision-disponible">
                <Wallet className="h-6 w-6 text-[#F5A623]" />
                <div className="text-2xl font-extrabold text-[#0A1F44] mt-3">{formatGs(mine?.disponible || 0)}</div>
                <div className="text-sm text-[#64748B]">Disponible para cobrar</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 shadow-sm">
              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-[#64748B]">Base de cálculo</span>
                <span className="font-semibold text-[#0A1F44]">
                  {mine?.base === "fijo" ? `${formatGs(mine?.comision_fija || 0)} por paquete` : `${((mine?.comision_pct || 0) * 100).toFixed(0)}% del flete`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mb-4">
                <span className="text-[#64748B]">Paquetes computados</span>
                <span className="font-semibold text-[#0A1F44]">{mine?.cantidad || 0}</span>
              </div>
              <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 mb-3 ${ventana.abierta ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}`}>
                <CalendarClock className="h-4 w-4" />
                {ventana.abierta ? "Ventana de cobro abierta (01 al 05)" : "El cobro solo se solicita del 01 al 05 de cada mes"}
              </div>
              <button onClick={solicitar} disabled={!ventana.abierta || loading || (mine?.disponible || 0) <= 0}
                data-testid="solicitar-pago-btn"
                className="w-full rounded-xl bg-[#F5A623] hover:bg-[#e0961a] text-[#0A1F44] font-bold py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Solicitar pago de comisión
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 font-semibold text-[#0A1F44]">Mis solicitudes</div>
              {solis.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-[#64748B]">Todavía no solicitaste pagos.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {solis.map((s) => {
                    const m = EST_META[s.estado] || EST_META.pendiente; const Icon = m.icon;
                    return (
                      <div key={s.id} className="px-4 py-3 flex items-center justify-between" data-testid={`mi-solicitud-${s.id}`}>
                        <div>
                          <div className="font-semibold text-[#0A1F44]">{formatGs(s.monto)}</div>
                          <div className="text-xs text-[#64748B]">Período {s.periodo}</div>
                        </div>
                        <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${m.cls}`}><Icon className="h-3.5 w-3.5" />{m.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Vista staff */}
        {staff && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-[#0A1F44]">Comisiones por colaborador</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[#64748B] text-xs uppercase">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Nombre</th>
                    <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Base</th>
                    <th className="text-right font-semibold px-4 py-3 hidden md:table-cell">Paquetes</th>
                    <th className="text-right font-semibold px-4 py-3">Comisión</th>
                    <th className="text-right font-semibold px-4 py-3 hidden sm:table-cell">Pagado</th>
                    <th className="text-right font-semibold px-4 py-3">Disponible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((c) => (
                    <tr key={c.cobrador_id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-semibold text-[#0A1F44]">{c.nombre}<div className="text-xs font-normal text-[#64748B]">{ROLE_LABELS[c.role] || c.role}</div></td>
                      <td className="px-4 py-3 hidden sm:table-cell text-[#64748B]">{c.base === "fijo" ? `${formatGs(c.comision_fija)}/paq.` : `${(c.comision_pct * 100).toFixed(0)}%`}</td>
                      <td className="px-4 py-3 text-right hidden md:table-cell text-[#64748B]">{c.cantidad}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">{formatGs(c.comision)}</td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell text-[#64748B]">{formatGs(c.pagado)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#F5A623]">{formatGs(c.disponible)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#64748B]">Sin comisiones acumuladas.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-100 text-xs text-[#64748B]">
              El pago y la aprobación de las solicitudes de comisión se hacen desde <b>Finanzas</b>.
            </div>
          </div>
        )}
      </main>
    </>
  );
}
